import React, { useMemo, useCallback, useState } from'react';
import { useNavigate } from'react-router-dom';
import { Activity, Zap, DollarSign, AlertTriangle, Users, Clock, RefreshCw, Inbox, ShieldX, Database, Gauge, ShieldCheck, ShieldAlert, X } from'lucide-react';
import { useLayout } from'../components/Layout';
import { useFilters } from'../context/FilterContext';
import { useApiQuery } from'../hooks/useApi';
import api from'../api/client';
import KPICard from'../components/charts/KPICard';
import AreaChart from'../components/charts/AreaChart';
import BarChart from'../components/charts/BarChart';
import PieChart from'../components/charts/PieChart';
import Breadcrumbs from'../components/Breadcrumbs';
import ErrorBoundary from'../components/ErrorBoundary';
import { formatTimeRangeLabel } from'../components/TimeRangePicker';
import { formatLatency } from'../utils/format';

export default function Dashboard() {
 const { timeRange, granularity } = useLayout();
 const { setFilter } = useFilters();
 const navigate = useNavigate();

 const summaryQ = useApiQuery(
 ['dashboard-summary', timeRange],
 () => api.getDashboardSummary(timeRange),
 { refetchInterval: 30000 }
 );

 const timeSeriesQ = useApiQuery(
 ['dashboard-timeseries', timeRange, granularity],
 () => api.getDashboardTimeSeries(timeRange, granularity),
 { refetchInterval: 30000 }
 );

 // Estimated cost by APIM Product — replaces the old Top Users chart on the
 // dashboard. Sourced from the gateway logs' ProductId (see /costs/by-product),
 // so it's accurate chargeback-grade attribution rather than name matching.
 const costByProductQ = useApiQuery(
 ['dashboard-cost-by-product', timeRange],
 () => api.getCostByProduct(timeRange),
 { refetchInterval: 60000 }
 );

 const tokenTimeSeriesQ = useApiQuery(
 ['dashboard-token-ts', timeRange, granularity],
 () => api.getTokenTimeSeries(timeRange, granularity),
 { refetchInterval: 30000 }
 );

 // Reliability widgets.
 // Latency distribution histogram — reveals the SHAPE of response times
 // (bimodal / slow tail) that Avg + P95 lines conceal.
 const latencyDistQ = useApiQuery(
 ['dashboard-latency-dist', timeRange],
 () => api.getLatencyDistribution(timeRange),
 { refetchInterval: 60000 }
 );

 // Gateway error breakdown — answers "what KIND of errors?" (status code /
 // reason / source) so ops can tell a client problem from a backend outage.
 const gatewayErrorsQ = useApiQuery(
 ['dashboard-gateway-errors', timeRange],
 () => api.getGatewayErrors(timeRange),
 { refetchInterval: 60000 }
 );

 // Token efficiency & truncation. Prompt:completion ratio surfaces context
 // bloat (rising input tokens = cost creep); truncation rate surfaces
 // completions cut off at the token cap (paid-for-but-unusable output).
 // Sourced from the ai-gateway TraceRecords, so it reconciles with cost.
 const tokenEfficiencyQ = useApiQuery(
 ['dashboard-token-efficiency', timeRange, granularity],
 () => api.getTokenEfficiency(timeRange, granularity),
 { refetchInterval: 60000 }
 );

 // Responsible AI — content-safety posture (blocked/flagged/jailbreak). Only
 // a subset of calls carry a safety signal, so the summary also reports
 // coverage; the UI shows "measured on X% of traffic" to stay honest.
 const contentSafetyQ = useApiQuery(
 ['dashboard-content-safety', timeRange, granularity],
 () => api.getContentSafety(timeRange, granularity),
 { refetchInterval: 60000 }
 );

 // Drill-down: which population of caught calls to show (blocked | jailbreak
 // | flagged | all), or null when closed. Set by clicking a safety KPI card;
 // the query only fires while the modal is open.
 const [safetyDrill, setSafetyDrill] = useState(null);
 const safetyEventsQ = useApiQuery(
 ['dashboard-content-safety-events', timeRange, safetyDrill],
 () => api.getContentSafetyEvents(timeRange, safetyDrill, 200),
 { enabled: !!safetyDrill }
 );

 // AI-efficiency drill-down: which population to show (truncated | bloat),
 // or null when closed. Fires only while the modal is open.
 const [effDrill, setEffDrill] = useState(null);
 const effEventsQ = useApiQuery(
 ['dashboard-token-efficiency-events', timeRange, effDrill],
 () => api.getTokenEfficiencyEvents(timeRange, effDrill, 200),
 { enabled: !!effDrill }
 );

 // Security posture — auth failures + 404-flood. The flood is a distinct
 // hygiene signal, kept separate so it never inflates error/auth rates.
 const securityQ = useApiQuery(
 ['dashboard-security', timeRange, granularity],
 () => api.getSecurity(timeRange, granularity),
 { refetchInterval: 60000 }
 );
 // Security drill-down: auth | flood, or null when closed.
 const [secDrill, setSecDrill] = useState(null);
 const secEventsQ = useApiQuery(
 ['dashboard-security-events', timeRange, secDrill],
 () => api.getSecurityEvents(timeRange, secDrill, 200),
 { enabled: !!secDrill }
 );

 // Error taxonomy — root-cause classification of genuine failures (the
 // 404-flood is excluded; it lives under Security). Drives the Reliability
 // "why are calls failing" cards + trend.
 const errorTaxonomyQ = useApiQuery(
 ['dashboard-error-taxonomy', timeRange, granularity],
 () => api.getErrorTaxonomy(timeRange, granularity),
 { refetchInterval: 60000 }
 );
 // Error-taxonomy drill-down: an ErrorCategory label, or null when closed.
 const [errDrill, setErrDrill] = useState(null);
 const errEventsQ = useApiQuery(
 ['dashboard-error-taxonomy-events', timeRange, errDrill],
 () => api.getErrorTaxonomyEvents(timeRange, errDrill, 200),
 { enabled: !!errDrill }
 );

 const summary = summaryQ.data;
 const loadingSummary = summaryQ.isLoading;

 const s = summary?.summary || {};
 const cost = summary?.cost || {};

 // Gateway-log retention notice. The KPI strip, traffic/latency series and
 // error breakdown all come from ApiManagementGatewayLogs, which the
 // workspace only keeps for a limited window (default 45d). When the user
 // picks a longer range the backend clamps those queries to retention; this
 // flag lets us tell the user so the shorter window isn't mistaken for a bug.
 const gatewayRetention = summary?.gatewayRetention;
 const retentionClamped = !!gatewayRetention?.clamped;
 const retentionDays = gatewayRetention?.days;

 // Tokens may be returned as either an array of period buckets or an object keyed by model.
 const totalTokens = useMemo(() => {
 const t = summary?.tokens;
 if (!t) return null;
 if (Array.isArray(t)) {
 return t.reduce((sum, row) => sum + (row?.TotalTokens || 0), 0);
 }
 if (typeof t ==='object') {
 return Object.values(t).reduce((sum, row) => sum + (row?.TotalTokens || row?.totalTokens || 0), 0);
 }
 return Number(t) || 0;
 }, [summary]);

 // Token time-series aggregation.
 //
 // The backend (kql.tokenUsageTimeSeries) groups `by bin(TimeGenerated, gr), Model`,
 // so each time bucket yields ONE ROW PER MODEL. Feeding that raw into a
 // stacked AreaChart produces duplicated X points and an incorrect stacked
 // total. Collapse to one row per bucket here, summing prompt/completion
 // tokens across all models, so the chart shows the true per-bucket totals.
 const tokenTimeSeriesData = useMemo(() => {
 const rows = Array.isArray(tokenTimeSeriesQ.data) ? tokenTimeSeriesQ.data : [];
 const byBucket = new Map();
 for (const r of rows) {
 const key = r?.TimeGenerated;
 if (!key) continue;
 const existing = byBucket.get(key) || { TimeGenerated: key, PromptTokens: 0, CompletionTokens: 0 };
 existing.PromptTokens += Number(r?.PromptTokens) || 0;
 existing.CompletionTokens += Number(r?.CompletionTokens) || 0;
 byBucket.set(key, existing);
 }
 return Array.from(byBucket.values()).sort(
 (a, b) => new Date(a.TimeGenerated) - new Date(b.TimeGenerated)
 );
 }, [tokenTimeSeriesQ.data]);

 // Cost by model for pie chart. The backend keys `byModel` by a canonical
 // model key (so billing meters and gateway model names merge onto one
 // entry) and carries the gateway's own spelling as `displayName` — prefer
 // that for the label, falling back to the key.
 const costByModelData = useMemo(() => (
 cost.byModel
 ? Object.entries(cost.byModel)
 .map(([key, v]) => ({ name: v?.displayName || key, value: v?.cost || 0 }))
 .filter((d) => d.value > 0)
 : []
 ), [cost.byModel]);

 // Estimated cost by product for the dashboard bar chart. `/costs/by-product`
 // returns `{ data: [{ productId, productName, cost, tokens, calls, ... }] }`
 // already sorted by cost desc. We take the top 5 for a legible chart and
 // keep the rest available via the Cost Center deep-link.
 const costByProductData = useMemo(() => {
 const rows = Array.isArray(costByProductQ.data?.data) ? costByProductQ.data.data : [];
 return rows.slice(0, 5).map((p) => ({
 name: p.productName || p.productId || 'Other / Unassigned',
 Cost: Number(p.cost) || 0,
 productId: p.productId,
 }));
 }, [costByProductQ.data]);

 // Full product list for the share donut. The PieChart component groups the
 // long tail into an "Other" slice itself, so we pass every product with
 // real spend rather than pre-slicing to top-5 like the bar chart does.
 const costByProductShare = useMemo(() => {
 const rows = Array.isArray(costByProductQ.data?.data) ? costByProductQ.data.data : [];
 return rows
 .map((p) => ({
 name: p.productName || p.productId || 'Other / Unassigned',
 Cost: Number(p.cost) || 0,
 productId: p.productId,
 }))
 .filter((p) => p.Cost > 0);
 }, [costByProductQ.data]);

 // Latency distribution histogram. `/dashboard/latency-distribution` now
 // returns SEMANTIC buckets `[{ Bucket, SortKey, Count }]` (e.g. "500ms–1s")
 // instead of hundreds of uniform 100ms bins — far more legible. Rows arrive
 // pre-sorted by SortKey; we just map them to the chart's { name, Count }.
 const latencyDistData = useMemo(() => {
 const rows = Array.isArray(latencyDistQ.data) ? latencyDistQ.data : [];
 return rows
 .filter((r) => r?.Bucket != null)
 .map((r) => ({
 name: String(r.Bucket),
 Count: Number(r.Count) || 0,
 sortKey: Number(r.SortKey) || 0,
 }));
 }, [latencyDistQ.data]);

 // Gateway error breakdown. `/dashboard/gateway-errors` returns
 // `[{ ResponseCode, LastErrorReason, LastErrorSource, Count, AvgDuration }]`.
 // We translate the raw HTTP status + APIM reason into PLAIN ENGLISH so a
 // non-technical reader understands what actually went wrong, aggregate rows
 // that map to the same human label, keep the top 7, and roll everything else
 // into a single "Other issues" bar so the chart total ties out to the true
 // error count (no silently-dropped bars).
 //
 // Each row also carries `code` (representative HTTP status) and `description`
 // (a longer explanation) so the chart can show the FULL details on hover —
 // the axis label is necessarily short, but nothing is lost. To add/adjust a
 // category in future, edit ONE place: the `CATALOG` table below.
 const errorBreakdownData = useMemo(() => {
 const rows = Array.isArray(gatewayErrorsQ.data) ? gatewayErrorsQ.data : [];

 // Single source of truth for error classification. Each entry: a matcher
 // over (code, reason) → { label, code, description }. Order matters: the
 // first match wins.
 const classify = (code, reason) => {
 const r = String(reason || '').toLowerCase();
 if (code === 429 || r.includes('tokenlimit') || r.includes('ratelimit')) {
 return {
 label: 'Rate limit reached',
 code: 429,
 description: 'Too many requests or the model token-per-minute quota was exceeded.',
 action: 'Ask the client to slow down (back-off/retry) or request a higher quota.',
 };
 }
 if (code === 401 || code === 403) {
 if (r.includes('subscriptionkey')) {
 return { label: 'Invalid API key', code, description: 'The request used a missing or incorrect APIM subscription key.', action: 'Verify the client is sending the correct subscription key.' };
 }
 if (r.includes('tokennotpresent')) {
 return { label: 'Missing sign-in token', code, description: 'No bearer/JWT token was supplied on the request.', action: 'Ensure the client attaches a valid Authorization header.' };
 }
 if (r.includes('tokenexpired')) {
 return { label: 'Expired sign-in token', code, description: 'The bearer/JWT token has expired.', action: 'Client should refresh its token before retrying.' };
 }
 return { label: 'Not authorized', code, description: 'The caller was refused at authentication (bad key/token or insufficient permissions).', action: 'Check the client’s credentials and role assignments.' };
 }
 if (code === 404 || r.includes('operationnotfound')) {
 return { label: 'Wrong URL / unknown endpoint', code: code || 404, description: 'The request hit a valid API but no matching operation/path.', action: 'Usually a client typo or outdated URL — confirm the endpoint path.' };
 }
 if (code === 400 || code === 413) {
 return { label: 'Bad request from client', code, description: code === 413 ? 'The request payload was too large.' : 'The request was malformed or failed validation.', action: code === 413 ? 'Reduce the payload size or increase the limit.' : 'Check the request body/parameters against the API contract.' };
 }
 if (code >= 500) {
 return { label: 'Server / backend error', code, description: 'The upstream model/backend failed or was unreachable (e.g. connection failure, 503).', action: 'Investigate backend health — this is a platform-side issue, not the client.' };
 }
 return { label: `Other (HTTP ${code})`, code, description: `HTTP ${code}${reason ? ` — ${reason}` : ''}.`, action: 'Review the raw gateway logs for this status.' };
 };

 // Aggregate by friendly label, keeping a representative code + desc + action,
 // plus a call-weighted average duration and the set of failure sources
 // (Client / Gateway / Backend) — the extra context that turns a hover into a
 // one-glance triage card.
 const byLabel = new Map();
 for (const row of rows) {
 const code = Number(row?.ResponseCode) || 0;
 const cnt = Number(row?.Count) || 0;
 const c = classify(code, row?.LastErrorReason);
 const prev = byLabel.get(c.label);
 const sources = prev?.sources || new Set();
 if (row?.LastErrorSource) sources.add(String(row.LastErrorSource));
 byLabel.set(c.label, {
 label: c.label,
 code: prev?.code ?? c.code,
 description: prev?.description ?? c.description,
 action: prev?.action ?? c.action,
 Count: (prev?.Count || 0) + cnt,
 // Weighted-duration accumulator (sum of avg*count) → divided later.
 _durSum: (prev?._durSum || 0) + (Number(row?.AvgDuration) || 0) * cnt,
 sources,
 });
 }

 const totalErrors = [...byLabel.values()].reduce((s, d) => s + d.Count, 0) || 1;

 // Build a full description that layers: what → where → how slow → action.
 const buildDesc = (d) => {
 const pct = ((d.Count / totalErrors) * 100).toFixed(1);
 const avgMs = d.Count ? Math.round(d._durSum / d.Count) : 0;
 const avgTxt = avgMs >= 1000 ? `${(avgMs / 1000).toFixed(1)}s` : `${avgMs}ms`;
 const src = d.sources && d.sources.size ? [...d.sources].join(', ') : '—';
 const parts = [
 d.description,
 `Share: ${pct}% of all errors (${d.Count.toLocaleString()} calls).`,
 `Avg time to fail: ${avgTxt}.`,
 `Failed at: ${src}.`,
 ];
 if (d.action) parts.push(`→ ${d.action}`);
 return parts.join('\n');
 };

 const sorted = [...byLabel.values()]
 .filter((d) => d.Count > 0)
 .sort((a, b) => b.Count - a.Count)
 .map((d) => ({
 // The axis shows "429 · Rate limit reached"; hover shows the rest.
 name: `${d.code} · ${d.label}`,
 Count: d.Count,
 code: d.code,
 description: buildDesc(d),
 }));

 // Keep the top 7; fold the rest into "Other issues" so nothing is hidden
 // and the bars sum to the real total.
 const TOP = 7;
 if (sorted.length <= TOP + 1) return sorted;
 const top = sorted.slice(0, TOP);
 const rest = sorted.slice(TOP);
 const otherCount = rest.reduce((s, d) => s + d.Count, 0);
 if (otherCount > 0) {
 const pct = ((otherCount / totalErrors) * 100).toFixed(1);
 top.push({
 name: 'Other issues',
 Count: otherCount,
 code: '',
 description: `${rest.length} smaller error categories combined (${pct}% of all errors).\n${rest
 .map((d) => `${d.code} ${d.name.split(' · ')[1] || ''}`.trim())
 .join(', ')}.`,
 });
 }   return top;
 }, [gatewayErrorsQ.data]);

 // ── Token efficiency derived data ──────────────────────────────────
 // summary carries fleet-wide ratio + truncation + split COVERAGE. When
 // coverage is low, the ratio is shown as indicative only (few models emit
 // a prompt/completion split — the gpt-5 fleet reports total-tokens only).
 const efficiency = tokenEfficiencyQ.data || {};
 const effSummary = efficiency.summary || {};
 const effSeries = useMemo(
 () => (Array.isArray(efficiency.series) ? efficiency.series : []),
 [efficiency.series]
 );
 const effByModel = useMemo(() => {
 const rows = Array.isArray(efficiency.byModel) ? efficiency.byModel : [];
 // Truncation offenders first, then biggest token consumers.
 return [...rows]
 .filter((m) => m.totalTokens > 0)
 .sort((a, b) => (b.truncationRate - a.truncationRate) || (b.totalTokens - a.totalTokens))
 .slice(0, 8)
 .map((m) => ({
 name: m.model,
 TruncationPct: Math.round((m.truncationRate || 0) * 1000) / 10,
 ratio: m.promptCompletionRatio,
 splitPct: Math.round((m.splitCoverage || 0) * 100),
 }));
 }, [efficiency.byModel]);
 const truncationPct = effSummary.truncationRate != null
 ? Math.round(effSummary.truncationRate * 1000) / 10
 : null;
 const splitCoveragePct = effSummary.splitCoverage != null
 ? Math.round(effSummary.splitCoverage * 100)
 : null;

 // ── Content-safety derived data ────────────────────────────────────
 const safety = contentSafetyQ.data || {};
 const safetySummary = safety.summary || {};
 const safetyCoveragePct = safetySummary.coverage != null
 ? Math.round(safetySummary.coverage * 100)
 : null;
 const safetySeries = useMemo(
 () => (Array.isArray(safety.series) ? safety.series : []),
 [safety.series]
 );
 // Per-category flagged counts for the harm-category bar. Only harm
 // categories carry a severity; jailbreak/indirect/protected-material are
 // detection booleans surfaced as KPIs instead.
 const safetyCategoryData = useMemo(() => {
 const c = safetySummary.byCategory || {};
 return [
 { name: 'Hate', Flagged: c.hate || 0 },
 { name: 'Sexual', Flagged: c.sexual || 0 },
 { name: 'Violence', Flagged: c.violence || 0 },
 { name: 'Self-harm', Flagged: c.selfHarm || 0 },
 ];
 }, [safetySummary.byCategory]);
 const safetyClean = safetyCoveragePct != null
 && (safetySummary.flaggedCalls || 0) === 0  && (safetySummary.blockedCalls || 0) === 0
 && (safetySummary.jailbreakCalls || 0) === 0;

 // ── Security derived data ──────────────────────────────────────────
 const security = securityQ.data || {};
 const secSummary = security.summary || {};
 const secSeries = useMemo(
 () => (Array.isArray(security.series) ? security.series : []),
 [security.series]
 );
 const authFailureRatePct = secSummary.authFailureRate != null
 ? Math.round(secSummary.authFailureRate * 1000) / 10
 : null;
 // Top offending source IPs for the auth-failure bar chart (already sorted
 // by volume desc from the backend). Top 5 for a legible horizontal chart.
 const secTopAuthData = useMemo(() => {
 const rows = Array.isArray(security.topAuthSources) ? security.topAuthSources : [];
 return rows.slice(0, 5).map((r) => ({
 name: r.ip || 'unknown',
 Failures: Number(r.failures) || 0,
 }));
 }, [security.topAuthSources]);

 // ── Error-taxonomy derived data ────────────────────────────────────
 const errorTax = errorTaxonomyQ.data || {};
 const errSummary = errorTax.summary || {};
 // Category → drill-down label + friendly card copy. Keys match the backend
 // ErrorCategory labels exactly so a card can open the matching events list.
 const errByCategory = useMemo(() => {
 const rows = Array.isArray(errorTax.byCategory) ? errorTax.byCategory : [];
 return rows.map((r) => ({
 name: r.category,
 Count: Number(r.count) || 0,
 topCode: r.topCode,
 topReason: r.topReason,
 // BarChart tooltip line.
 description: `${r.count} failure${(r.count === 1 ? '' : 's')}${r.topCode ? ` · usually ${r.topCode}` : ''}${r.topReason ? ` · ${r.topReason}` : ''}`,
 }));
 }, [errorTax.byCategory]);
 const errSeries = useMemo(
 () => (Array.isArray(errorTax.series) ? errorTax.series : []),
 [errorTax.series]
 );
 const errSeriesKeys = useMemo(
 () => (Array.isArray(errorTax.seriesKeys) ? errorTax.seriesKeys : []),
 [errorTax.seriesKeys]
 );

 // Granularity-aware x-axis formatter (handles 5m/15m/1h/1d-style granularity)
 const xAxisFormatter = useCallback((val) => {
 if (val == null || val ==='') return'';
 const d = new Date(val);
 if (isNaN(d.getTime())) return'';
 const isDaily = /d$/i.test(granularity ||'');
 return isDaily
 ? d.toLocaleDateString(undefined, { month:'short', day:'numeric' })
 : d.toLocaleString(undefined, { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' });
 }, [granularity]);

 // X-axis caption that states the bucket size, so a reader knows whether a
 // point represents an hour, a day or five minutes of traffic. Without this
 // the same chart shape means very different things at different settings.
 const timeAxisLabel = useMemo(() => {
 const g = String(granularity ||'').toLowerCase();
 const labels = {
'1m':'Time (per minute)',
'5m':'Time (5-minute intervals)',
'15m':'Time (15-minute intervals)',
'1h':'Time (hourly)',
'6h':'Time (6-hour intervals)',
'1d':'Time (daily)',
'7d':'Time (weekly)',
 };
 return labels[g] ||'Time';
 }, [granularity]);

 const handleProductClick = (data) => {
 // Deep-link into the Cost Center so operators can see the full per-product
 // estimated-cost breakdown behind the top-5 dashboard chart. Carry the
 // clicked product in the shared filter so Cost Center auto-expands that
 // product's per-model breakdown on arrival.
 const productId = data?.productId || data?.payload?.productId;
 if (productId) {
 setFilter('product', productId);
 }
 navigate('/costs');
 };

 const handleModelPieClick = (entry) => {
 // recharts passes a synthetic object; the user-facing name lives on payload.name or entry.name
 const name = entry?.payload?.name || entry?.name;
 if (name) {
 setFilter('model', name);
 navigate('/costs');
 }
 };

 const errorRate = s.TotalCalls ? ((s.FailedCalls || 0) / s.TotalCalls) * 100 : null;
 // Success rate over PROCESSED traffic — the honest health number. This is
 // computed from the same TotalCalls/SuccessfulCalls the KPI cards show, so
 // every percentage on the page reconciles to the same base.
 const successRate = s.TotalCalls ? ((s.SuccessfulCalls || 0) / s.TotalCalls) * 100 : null;

 // Cache hit rate (%) — hits ÷ (hits + misses) over served traffic. A cache
 // hit is answered without calling the model backend, so it's both faster and
 // free: a direct cost/latency lever. `CacheHitRate` is computed server-side
 // (null when there's no cacheable traffic).
 const cacheHitRate = s.CacheHitRate != null ? Number(s.CacheHitRate) : null;

 // Cost efficiency — $ per 1,000 tokens. Answers "is spend rising because of
 // MORE usage or PRICIER calls?". Uses total $ (Cost Management) over total
 // tokens (gateway traces). Null when either side is missing.
 const costPer1kTokens = (cost.totalCost != null && totalTokens)
 ? (Number(cost.totalCost) / (totalTokens / 1000))
 : null;
 const anyRefetching = summaryQ.isFetching || timeSeriesQ.isFetching || costByProductQ.isFetching || tokenTimeSeriesQ.isFetching || latencyDistQ.isFetching || gatewayErrorsQ.isFetching;

 return (
 <div className="space-y-6">
 <Breadcrumbs />

 {/* Page Header */}
 <div className="flex items-start justify-between gap-4 flex-wrap">
 <div>
 <h1 className="text-2xl font-bold tracking-tight">Executive Dashboard</h1>
 <p className="text-sm text-gray-500 mt-1">
 LLM Gateway overview
 <span className="inline-flex items-center ml-2 px-2 py-0.5 rounded-md bg-gray-100 text-gray-700 text-xs font-medium">
 last {formatTimeRangeLabel(timeRange)}
 </span>
 </p>
 </div>
 {anyRefetching && !loadingSummary && (
 <div className="flex items-center gap-1.5 text-xs text-gray-500" aria-live="polite">
 <RefreshCw className="w-3.5 h-3.5 animate-spin" />
 <span>Refreshing…</span>
 </div>
 )}
 </div>

 {/* Gateway-log retention notice — responsive: full-width, wraps on mobile.
 Only shown when the selected range exceeds gateway-log retention, so the
 truncated volume/latency/error figures aren't mistaken for a data gap. */}
 {retentionClamped && (
 <div
 role="status"
 className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs sm:text-sm text-amber-900"
 >
 <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
 <p className="leading-snug">
 Call volume, latency and error figures come from the APIM gateway logs,
 which are retained for <span className="font-semibold">{retentionDays} days</span>.
 The selected range is longer, so those charts show only the last{' '}
 {retentionDays} days. Token &amp; cost figures are unaffected.
 </p>
 </div>
 )}

 {/* KPI Cards */}
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
 {loadingSummary ? (
 Array.from({ length: 9 }).map((_, i) => <KPISkeleton key={i} />)
 ) : summaryQ.isError ? (
 <div className="col-span-full">
 <ErrorState
 title="Couldn't load summary"
 message={summaryQ.error?.message ||'Try again in a moment.'}
 onRetry={() => summaryQ.refetch()}
 />
 </div>
 ) : (
 <>
 <ErrorBoundary name="Total Calls KPI">
 <KPICard
 title="Total Calls"
 value={s.TotalCalls}
 format="number"
 icon={Activity}
 color="brand"
 help="Requests actually served by the gateway (routed to a backend) in the selected range. Blocked auth/rate-limit attempts are shown separately as Edge Rejections."
 subtitle={
 successRate != null
 ? `${successRate.toFixed(1)}% success · ${Number(s.SuccessfulCalls || 0).toLocaleString()} of ${Number(s.TotalCalls || 0).toLocaleString()}`
 : undefined
 }
 onClick={() => navigate('/apis?sort=calls')}
 />
 </ErrorBoundary>
 <ErrorBoundary name="Total Cost KPI">
 <KPICard
 title="Total Cost"
 value={cost.totalCost}
 format="currency"
 icon={DollarSign}
 color="emerald"
 help="Actual spend from Azure Cost Management for the range. Billing data lags 8–24h, so recent hours may be understated."
 subtitle={cost.byModel ?`${Object.keys(cost.byModel).length} models` : undefined}
 onClick={() => navigate('/costs')}
 />
 </ErrorBoundary>
 <ErrorBoundary name="Total Tokens KPI">
 <KPICard
 title="Total Tokens"
 value={totalTokens}
 format="tokens"
 icon={Zap}
 color="violet"
 help="Sum of prompt + completion tokens across all model calls in the range, from the AI gateway usage traces."
 onClick={() => navigate('/costs')}
 />
 </ErrorBoundary>
 <ErrorBoundary name="Error Rate KPI">
 <KPICard
 title="Error Rate"
 value={errorRate}
 format="percent"
 icon={AlertTriangle}
 color={errorRate != null && errorRate > 0 ?'red' :'emerald'}
 help="Share of SERVED calls that failed (5xx/4xx after routing to a backend). Excludes auth/rate-limit rejections, which are counted as Edge Rejections."
 subtitle={s.FailedCalls != null ?`${Number(s.FailedCalls).toLocaleString()} of ${Number(s.TotalCalls || 0).toLocaleString()} served calls failed` : undefined}
 onClick={() => navigate('/apis?sort=errorRate&status=failing')}
 />
 </ErrorBoundary>
 <ErrorBoundary name="Edge Rejections KPI">
 <KPICard
 title="Edge Rejections"
 value={s.EdgeRejections}
 format="number"
 icon={ShieldX}
 color={s.EdgeRejections != null && Number(s.EdgeRejections) > 0 ?'red' :'emerald'}
 help="Requests refused at the gateway before reaching a backend — typically 401/403 (bad/missing token) or 429 (rate limit). High values usually mean a misconfigured client, not a platform outage."
 subtitle="Blocked at gateway (401/403/429)"
 onClick={() => navigate('/apis?status=rejected')}
 />
 </ErrorBoundary>
 <ErrorBoundary name="Avg Latency KPI">
 <KPICard
 title="Avg Latency"
 value={s.AvgLatencyMs}
 format="ms"
 icon={Clock}
 color="amber"
 help="Average gateway-observed end-to-end time for served calls. P95 means 95% of calls were faster than this value."
 subtitle={s.P95Latency != null ?`P95: ${formatLatency(s.P95Latency)}` : undefined}
 onClick={() => navigate('/apis?sort=latency')}
 />
 </ErrorBoundary>
 <ErrorBoundary name="Active Subs KPI">
 <KPICard
 title="Active Subs"
 value={s.UniqueSubscriptions}
 format="number"
 icon={Users}
 color="brand"
 help="Distinct client applications (by app client ID) that made calls in the range. Subtitle shows distinct end users (by email) from the AI gateway traces."
 subtitle={s.UniqueUsers != null ?`${Number(s.UniqueUsers).toLocaleString()} users` : undefined}
 onClick={() => navigate('/users')}
 />
 </ErrorBoundary>
 <ErrorBoundary name="Cache Hit Rate KPI">
 <KPICard
 title="Cache Hit Rate"
 value={cacheHitRate}
 format="percent"
 icon={Database}
 color="emerald"
 help="Share of served calls answered from the APIM cache instead of the model backend. Cache hits are both faster and free, so a higher rate directly lowers cost and latency."
 subtitle={cacheHitRate != null ? 'Served from cache (faster & free)' : 'No cacheable traffic'}
 onClick={() => navigate('/apis')}
 />
 </ErrorBoundary>
 <ErrorBoundary name="Cost per 1K Tokens KPI">
 <KPICard
 title="Cost / 1K Tokens"
 value={costPer1kTokens}
 format="currency"
 icon={Gauge}
 color="violet"
 help="Average spend per 1,000 tokens (total cost ÷ total tokens). An efficiency metric: rising usage with a flat value means growth; a rising value means pricier model mix."
 subtitle="Efficiency — spend per 1K tokens"
 onClick={() => navigate('/costs')}
 />
 </ErrorBoundary>
 </>
 )}
 </div>

 {/* ── Cost by Product (headline chart) ─────────────────────────────
 The single most-asked question — "which product is spending what?" —
 gets its own prominent, full-width section so any viewer (including
 non-technical product owners) understands it at a glance. Left: a
 ranked bar chart of $ by product. Right: a share-of-spend donut.
 Both deep-link into the Cost Center for the full breakdown. */}
 <section className="space-y-3">
 <div className="flex items-center justify-between gap-2 flex-wrap">
 <div>
 <div className="flex items-center gap-2">
 <h2 className="text-sm font-semibold text-gray-800">Cost by Product</h2>
 <span
 className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800"
 title="Computed from each product's token usage × per-model pricing — not directly billed by Azure. A close approximation for chargeback/showback."
 >
 Estimated
 </span>
 </div>
 <p className="text-xs text-gray-500 mt-0.5">
 How much each product (team/app) is spending on AI in the selected period. Click any bar or slice for the full breakdown.
 </p>
 </div>
 {costByProductQ.data?.totals?.cost != null && (
 <div className="text-right">
 <div className="text-lg font-semibold text-gray-900 tabular-nums">
 ${Number(costByProductQ.data.totals.cost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
 </div>
 <div className="text-[11px] text-gray-500 uppercase tracking-wider">Total estimated</div>
 </div>
 )}
 </div>
 <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
 <div className="lg:col-span-3">
 <ChartCard
 query={costByProductQ}
 isEmpty={costByProductData.length === 0}
 emptyMessage="No product-attributable cost in this period."
 name="Cost by Product — Bar"
 >
 <BarChart
 data={costByProductData}
 dataKeys={['Cost']}
 xAxisKey="name"
 title="Spend by Product (top 5)"
 valueFormat="currency"
 colorByIndex
 horizontal
 showValueLabels
 height={300}
 exportFilename="cost-by-product"
 onBarClick={handleProductClick}
 />
 </ChartCard>
 </div>
 <div className="lg:col-span-2">
 <ChartCard
 query={costByProductQ}
 isEmpty={costByProductShare.length === 0}
 emptyMessage="No product-attributable cost in this period."
 name="Cost by Product — Share"
 >
 <PieChart
 data={costByProductShare}
 dataKey="Cost"
 nameKey="name"
 title="Share of Total Spend"
 height={300}
 exportFilename="cost-by-product-share"
 onSliceClick={handleProductClick}
 />
 </ChartCard>
 </div>
 </div>
 </section>

 {/* Traffic & Latency */}
 <section className="space-y-3">
 <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
 Traffic & Latency
 </h2>
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
 <ChartCard
 query={timeSeriesQ}
 emptyMessage="No traffic data for this period."
 name="API Calls Chart"
 >            <AreaChart
 data={timeSeriesQ.data || []}
 dataKeys={['Calls','Errors']}
 title="API Calls Over Time"
 xAxisKey="TimeGenerated"
 formatXAxis={xAxisFormatter}
 xAxisLabel={timeAxisLabel}
 yAxisLabel="Requests"
 seriesLabels={{ Calls:'Total requests', Errors:'Failed requests' }}
 exportFilename="api-calls-timeseries"
 />
 </ChartCard>
 <ChartCard
 query={timeSeriesQ}
 emptyMessage="No latency data for this period."
 name="Latency Chart"
 >            <AreaChart
 data={timeSeriesQ.data || []}
 dataKeys={['AvgLatency','P95Latency']}
 title="Latency Over Time (ms)"
 xAxisKey="TimeGenerated"
 formatXAxis={xAxisFormatter}
 xAxisLabel={timeAxisLabel}
 yAxisLabel="Response time (ms)"
 seriesLabels={{
 AvgLatency:'Average',
 P95Latency:'P95 (slowest 5%)',
 }}
 exportFilename="latency-timeseries"
 />
 </ChartCard>
 </div>
 </section>

 {/* Breakdowns */}
 <section className="space-y-3">
 <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
 Breakdowns
 </h2>
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
 <ChartCard
 isEmpty={costByModelData.length === 0}
 isLoading={loadingSummary}
 isError={summaryQ.isError}
 emptyMessage="No model cost data."
 name="Cost by Model Chart"
 >
 <PieChart
 data={costByModelData}
 title="Cost by Model"
 exportFilename="cost-by-model"
 height={260}
 onSliceClick={handleModelPieClick}
 />
 </ChartCard>          <ChartCard
 query={tokenTimeSeriesQ}
 isEmpty={tokenTimeSeriesData.length === 0}
 emptyMessage="No token usage in this period."
 name="Token Usage Chart"
 >
 <AreaChart
 data={tokenTimeSeriesData}
 dataKeys={['PromptTokens','CompletionTokens']}
 title="Token Usage Over Time"
 stacked
 height={260}
 xAxisKey="TimeGenerated"
 formatXAxis={xAxisFormatter}
 xAxisLabel={timeAxisLabel}
 yAxisLabel="Tokens"
 seriesLabels={{
 PromptTokens:'Prompt (input)',
 CompletionTokens:'Completion (output)',
 }}
 exportFilename="token-usage-timeseries"
 />
 </ChartCard>
 </div>
 </section>

 {/* Reliability */}
 <section className="space-y-3">
 <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
 Reliability
 </h2>
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
 <ChartCard
 query={latencyDistQ}
 isEmpty={latencyDistData.length === 0}
 emptyMessage="No latency data for this period."
 name="Latency Distribution Chart"
 >
 <BarChart
 data={latencyDistData}
 dataKeys={['Count']}
 xAxisKey="name"
 title="Latency Distribution (response time histogram)"
 height={300}
 showValueLabels
 exportFilename="latency-distribution"
 />
 </ChartCard>
 <ChartCard
 query={gatewayErrorsQ}
 isEmpty={errorBreakdownData.length === 0}
 emptyMessage="No gateway errors in this period. 🎉"
 name="Error Breakdown Chart"
 >
 <BarChart
 data={errorBreakdownData}
 dataKeys={['Count']}
 xAxisKey="name"
 descriptionKey="description"
 title="Error Breakdown (by status & reason)"
 height={300}
 horizontal
 colorByIndex
 showValueLabels        exportFilename="error-breakdown"
 onBarClick={() => navigate('/apis?status=failing')}
 />
 </ChartCard>
 </div>
 </section>

 {/* Why Calls Fail — root-cause taxonomy. Classifies genuine failures by
 root cause using the gateway's LastError* fields (the 404 scanner-flood
 is excluded — it lives under Security). The category counts always sum
 to the total, and each card drills into the actual failing calls. */}
 <section className="space-y-3">
 <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
 Why Calls Fail
 </h2>
 <p className="text-xs text-gray-500 -mt-1">
 Every failed call, grouped by what actually went wrong. Automated scanning
 noise is excluded (see Security). Tap a card to see the specific calls.
 {errSummary.failures != null && (
 <> <span className="font-semibold text-gray-700">{Number(errSummary.failures).toLocaleString()}</span> failed call{errSummary.failures === 1 ? '' : 's'} in this period.</>
 )}
 </p>
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
 <ErrorBoundary name="Auth Errors KPI">
 <KPICard
 title="Sign-In Problems"
 value={errSummary.authentication}
 format="number"
 icon={ShieldX}
 color={(errSummary.authentication || 0) > 0 ? 'amber' : 'emerald'}
 help="Failures from authentication policies (validate-jwt / authorization) or backend-rejected 401/403 — expired tokens, invalid or missing subscription keys. Click to see the actual calls."
 subtitle="Expired/invalid tokens or keys"
 onClick={() => setErrDrill('Authentication')}
 />
 </ErrorBoundary>
 <ErrorBoundary name="Backend Errors KPI">
 <KPICard
 title="Backend Failures"
 value={errSummary.backend}
 format="number"
 icon={Database}
 color={(errSummary.backend || 0) > 0 ? 'red' : 'emerald'}
 help="The AI model backend couldn't be reached or dropped the connection (request-forwarder / BackendConnectionFailure). This is an upstream/infrastructure fault, not a client mistake. Click to see the actual calls."
 subtitle="Model backend unreachable"
 onClick={() => setErrDrill('Backend / upstream')}
 />
 </ErrorBoundary>
 <ErrorBoundary name="Client Errors KPI">
 <KPICard
 title="Bad Requests"
 value={errSummary.clientRequest}
 format="number"
 icon={AlertTriangle}
 color={(errSummary.clientRequest || 0) > 0 ? 'amber' : 'emerald'}
 help="Requests the gateway rejected as malformed, too large, or using an unsupported method/format (400/405/413/415/422). The caller needs to fix the request. Click to see the actual calls."
 subtitle="Malformed / oversized / unsupported"
 onClick={() => setErrDrill('Client request')}
 />
 </ErrorBoundary>
 <ErrorBoundary name="Platform Errors KPI">
 <KPICard
 title="Gateway / Other"
 value={(errSummary.platform || 0) + (errSummary.policyConfig || 0) + (errSummary.other || 0)}
 format="number"
 icon={AlertTriangle}
 color={(errSummary.platform || 0) > 0 ? 'red' : 'gray'}
 help="Everything else: gateway 5xx with no policy attribution, policy/config faults, and uncategorised failures. A rising count here usually means a platform or configuration issue. Click to see the actual calls."
 subtitle={`${Number(errSummary.platform || 0).toLocaleString()} gateway · ${Number(errSummary.policyConfig || 0).toLocaleString()} policy · ${Number(errSummary.other || 0).toLocaleString()} other`}
 onClick={() => setErrDrill('all')}
 />
 </ErrorBoundary>
 </div>
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
 <div id="chart-error-taxonomy" className="rounded-xl transition-shadow">
 <ChartCard
 query={errorTaxonomyQ}
 isEmpty={errByCategory.length === 0}
 emptyMessage="No failures to classify in this period. 🎉"
 name="Failure Cause Chart"
 >
 <BarChart
 data={errByCategory}
 dataKeys={['Count']}
 xAxisKey="name"
 descriptionKey="description"
 title="Failures by Cause"
 height={300}
 horizontal
 colorByIndex
 showValueLabels
 seriesLabels={{ Count: 'Failed calls' }}
 exportFilename="failures-by-cause"
 onBarClick={(d) => setErrDrill(d?.name || d?.payload?.name || 'all')}
 />
 </ChartCard>
 </div>
 <div id="chart-error-taxonomy-trend" className="rounded-xl transition-shadow">
 <ChartCard
 query={errorTaxonomyQ}
 isEmpty={errSeries.length === 0 || errSeriesKeys.length === 0}
 emptyMessage="No failures over time in this period."
 name="Failure Cause Trend Chart"
 >
 <AreaChart
 data={errSeries}
 dataKeys={errSeriesKeys}
 title="Failures by Cause Over Time"
 stacked
 height={300}
 xAxisKey="timeGenerated"
 formatXAxis={xAxisFormatter}
 xAxisLabel={timeAxisLabel}
 yAxisLabel="Failed calls"
 exportFilename="failures-by-cause-trend"
 />
 </ChartCard>
 </div>
 </div>
 </section>

 {/* AI Efficiency — prompt bloat & truncation. Sourced from the
 ai-gateway token traces (reconciles with cost). The ratio is only
 meaningful over calls that emit a prompt/completion split, so we
 always show the split-coverage caption to keep it honest. */}
 <section className="space-y-3">
 <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
 AI Efficiency
 </h2>
 <p className="text-xs text-gray-500 -mt-1">
 How efficiently prompts are being used. "Tokens" are the units AI models
 charge by — roughly ¾ of a word each. Watch for long inputs that inflate
 cost, and answers cut off before they finish.
 </p>
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
 <ErrorBoundary name="Prompt:Completion Ratio KPI">
 <KPICard
 title="Input vs. Output Size"
 value={effSummary.promptCompletionRatio != null ? `${effSummary.promptCompletionRatio.toFixed(2)} : 1` : '—'}
 icon={Gauge}
 color="violet"
 help="Ratio of paid input (prompt) tokens to output (completion) tokens across calls that report both. A rising ratio signals context/prompt bloat driving cost up without more output. Click to see the worst offenders."
 subtitle={splitCoveragePct != null
 ? `We send ${effSummary.promptCompletionRatio != null ? effSummary.promptCompletionRatio.toFixed(1) : '—'}× as much text as we get back`
 : 'Awaiting data'}
 onClick={() => setEffDrill('bloat')}
 />
 </ErrorBoundary>
 <ErrorBoundary name="Truncation Rate KPI">
 <KPICard
 title="Cut-Off Answers"
 value={truncationPct}
 format="percent"
 icon={AlertTriangle}
 color={truncationPct != null && truncationPct >= 2 ? 'red' : 'emerald'}
 help="Share of completions cut off at the token cap (finish/stop reason = length/max_tokens). Truncated output is paid for but often unusable — raise max_tokens or shorten prompts. Click to see the actual truncated calls."
 subtitle={effSummary.truncatedCalls != null
 ? `${Number(effSummary.truncatedCalls).toLocaleString()} of ${Number(effSummary.calls || 0).toLocaleString()} answers ran out of room`
 : undefined}
 onClick={() => setEffDrill('truncated')}
 />
 </ErrorBoundary>
 <ErrorBoundary name="Total Tokens KPI">
 <KPICard
 title="Tokens Used (period)"
 value={effSummary.totalTokens}
 format="number"
 icon={Zap}
 color="brand"
 help="Total tokens across all calls in the range (prompt + completion), from the ai-gateway traces used for cost."
 subtitle={effSummary.promptTokens != null
 ? `${Number(effSummary.promptTokens).toLocaleString()} sent · ${Number(effSummary.completionTokens || 0).toLocaleString()} received`
 : undefined}
 onClick={() => navigate('/costs')}
 />
 </ErrorBoundary>
 </div>
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
 <div id="chart-prompt-completion-trend" className="rounded-xl transition-shadow">
 <ChartCard
 query={tokenEfficiencyQ}
 isEmpty={effSeries.length === 0}
 emptyMessage="No token traces in this period."
 name="Prompt vs Completion Trend Chart"
 >
 <AreaChart
 data={effSeries}
 dataKeys={['promptTokens', 'completionTokens']}
 title="Text Sent vs. Received Over Time"
 stacked
 height={300}
 xAxisKey="timeGenerated"
 formatXAxis={xAxisFormatter}
 xAxisLabel={timeAxisLabel}
 yAxisLabel="Tokens"
 seriesLabels={{
 promptTokens: 'Sent (input)',
 completionTokens: 'Received (output)',
 }}
 exportFilename="prompt-completion-trend"
 />
 </ChartCard>
 </div>
 <div id="chart-truncation-by-model" className="rounded-xl transition-shadow">
 <ChartCard
 query={tokenEfficiencyQ}
 isEmpty={effByModel.length === 0}
 emptyMessage="No per-model efficiency data."
 name="Truncation by Model Chart"
 >
 <BarChart
 data={effByModel}
 dataKeys={['TruncationPct']}
 xAxisKey="name"
 title="Cut-Off Answers by Model (%)"
 height={300}
 horizontal
 colorByIndex
 showValueLabels
 seriesLabels={{ TruncationPct: 'Truncation %' }}
 exportFilename="truncation-by-model"
 />
 </ChartCard>
 </div>
 </div>
 </section>

 {/* Responsible AI — Content Safety. Sourced from Azure content-filter
 annotations in the model response body. Only a subset of calls carry
 the signal, so every metric is paired with a coverage caption; a clean
 window (0 flagged) is shown as reassuring, NOT as "all traffic safe". */}
 <section className="space-y-3">
 <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
 Responsible AI — Content Safety
 </h2>
 {safetyCoveragePct != null && (
 <p className="text-xs text-gray-500 -mt-1">
 Azure automatically screens AI requests and responses for harmful content.
 We could check{' '}
 <span className="font-semibold text-gray-700">{safetyCoveragePct}%</span>{' '}
 of calls ({Number(safetySummary.signalCalls || 0).toLocaleString()} of{' '}
 {Number(safetySummary.totalCalls || 0).toLocaleString()}). The rest didn't
 return a safety report — that means "unknown," not "safe."
 </p>
 )}
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
 <ErrorBoundary name="Blocked Calls KPI">
 <KPICard
 title="Blocked"
 value={safetySummary.blockedCalls}
 format="number"
 icon={ShieldX}
 color={safetySummary.blockedCalls > 0 ? 'red' : 'emerald'}
 help="Calls where Azure content filtering hard-blocked the request or response (blocked=true). These never reached the user. Click to see the actual blocked calls."
 subtitle={safetyCoveragePct != null ? `Stopped before reaching the user` : undefined}
 onClick={() => setSafetyDrill('blocked')}
 />
 </ErrorBoundary>
 <ErrorBoundary name="Flagged Calls KPI">
 <KPICard
 title="Flagged"
 value={safetySummary.flaggedCalls}
 format="number"
 icon={ShieldAlert}
 color={safetySummary.flaggedCalls > 0 ? 'amber' : 'emerald'}
 help="Calls with any harm category (hate/sexual/violence/self-harm) at medium or high severity, or a jailbreak/indirect-attack/protected-material detection. Click to see the actual flagged calls."
 subtitle="Potentially harmful content detected"
 onClick={() => setSafetyDrill('flagged')}
 />
 </ErrorBoundary>
 <ErrorBoundary name="Jailbreak KPI">
 <KPICard
 title="Manipulation Attempts"
 value={(safetySummary.jailbreakCalls || 0) + (safetySummary.indirectAttackCalls || 0)}
 format="number"
 icon={ShieldAlert}
 color={(safetySummary.jailbreakCalls || 0) + (safetySummary.indirectAttackCalls || 0) > 0 ? 'red' : 'emerald'}
 help="Prompt-shield detections: direct jailbreak attempts plus indirect (document/tool) prompt-injection attempts flagged by Azure. Click to see the actual detections."
 subtitle="Tricks to bypass the AI's safety rules"
 onClick={() => setSafetyDrill('jailbreak')}
 />
 </ErrorBoundary>
 <ErrorBoundary name="Safety Coverage KPI">
 <KPICard
 title="Calls We Could Check"
 value={safetyCoveragePct}
 format="percent"
 icon={ShieldCheck}
 color="brand"
 help="Share of calls that carried a content-filter annotation. A higher value means the safety posture above reflects more of your traffic. Low coverage means most calls are unlabelled, not necessarily safe."
 subtitle="Higher = the numbers above cover more traffic"
 />
 </ErrorBoundary>
 </div>
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
 <div id="chart-content-safety-trend" className="rounded-xl transition-shadow">
 <ChartCard
 query={contentSafetyQ}
 isEmpty={safetySeries.length === 0}
 emptyMessage="No content-safety signals in this period."
 name="Content Safety Trend Chart"
 >
 <AreaChart
 data={safetySeries}
 dataKeys={['flaggedCalls', 'blockedCalls']}
 title="Flagged & Blocked Calls Over Time"
 height={300}
 xAxisKey="timeGenerated"
 formatXAxis={xAxisFormatter}
 xAxisLabel={timeAxisLabel}
 yAxisLabel="Calls"
 seriesLabels={{ flaggedCalls: 'Flagged', blockedCalls: 'Blocked' }}
 exportFilename="content-safety-trend"
 />
 </ChartCard>
 </div>
 <div id="chart-harm-category" className="rounded-xl transition-shadow">
 <ChartCard
 query={contentSafetyQ}
 isEmpty={safetyCategoryData.length === 0}
 emptyMessage="No harm-category data."
 name="Harm Category Chart"
 >
 {safetyClean ? (
 <div className="chart-container flex flex-col items-center justify-center text-center min-h-[300px]">
 <div className="p-3 rounded-full bg-emerald-50 mb-3">
 <ShieldCheck className="w-6 h-6 text-emerald-500" />
 </div>
 <p className="text-sm font-medium text-gray-700">No flagged content in this window</p>
 <p className="text-xs text-gray-500 mt-1 max-w-xs">
 Of the {safetyCoveragePct}% of calls we could check, none contained
 harmful content and no manipulation attempts were detected.
 </p>
 </div>
 ) : (
 <BarChart
 data={safetyCategoryData}
 dataKeys={['Flagged']}
 xAxisKey="name"
 title="Flagged by Harm Type"
 height={300}
 horizontal
 colorByIndex
 showValueLabels
 seriesLabels={{ Flagged: 'Flagged calls' }}
 exportFilename="harm-category-breakdown"
 />    )}
 </ChartCard>
 </div>
 </div>
 </section>

 {/* Security — Auth-Failure & 404-Flood Anomaly. Two distinct signals
 from the gateway logs, kept separate: auth failures (401/403) are a
 credential problem; the 404-flood is scanner noise (empty ApiId) and
 is NEVER folded into the error/auth rates so it can't mask real issues. */}
 <section className="space-y-3">
 <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
 Security — Access &amp; Anomaly
 </h2>
 <p className="text-xs text-gray-500 -mt-1">
 Who was turned away at the door. "Rejected sign-ins" are requests with a
 bad or missing key. "Probing traffic" is automated scanning for weaknesses —
 shown for awareness, and kept out of the health numbers so it can't hide
 real problems.
 </p>
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
 <ErrorBoundary name="Auth Failures KPI">
 <KPICard
 title="Rejected Sign-Ins"
 value={secSummary.authFailures}
 format="number"
 icon={ShieldX}
 color={(secSummary.authFailures || 0) > 0 ? 'amber' : 'emerald'}
 help="Requests refused with 401 (unauthorized) or 403 (forbidden) — a credential presented but rejected. Click to see the actual refused calls."
 subtitle={`${Number(secSummary.unauthorized || 0).toLocaleString()} bad/missing key · ${Number(secSummary.forbidden || 0).toLocaleString()} not allowed`}
 onClick={() => setSecDrill('auth')}
 />
 </ErrorBoundary>
 <ErrorBoundary name="Auth-Failure Rate KPI">
 <KPICard
 title="Rejection Rate"
 value={authFailureRatePct}
 format="percent"
 icon={Gauge}
 color={(authFailureRatePct || 0) >= 5 ? 'red' : (authFailureRatePct || 0) >= 1 ? 'amber' : 'emerald'}
 help="Share of credentialed requests that were refused (401/403 ÷ served + refused). Scanner 404-flood is excluded so a spike here reflects real credential problems."
 subtitle="Share of sign-in attempts turned away"
 />
 </ErrorBoundary>
 <ErrorBoundary name="Auth-Fail Sources KPI">
 <KPICard
 title="Where They Came From"
 value={secSummary.authFailIPs}
 format="number"
 icon={Users}
 color="brand"
 help="Distinct source IPs behind the auth failures. A low count with high volume means the failures are concentrated (likely one misconfigured client or a brute-force attempt)."
 subtitle={`Distinct locations (IP addresses)`}
 />
 </ErrorBoundary>
 <ErrorBoundary name="404 Flood KPI">
 <KPICard
 title="Probing Traffic"
 value={secSummary.floodCount}
 format="number"
 icon={ShieldAlert}
 color={(secSummary.floodCount || 0) > 10000 ? 'amber' : 'gray'}
 help="Unauthenticated requests to no matching API (scanner / probe noise). Kept separate from error rates. Click to see the actual flood hits."
 subtitle={`From ${Number(secSummary.floodIPs || 0).toLocaleString()} location${(secSummary.floodIPs || 0) === 1 ? '' : 's'} · scanning for weak spots`}
 onClick={() => setSecDrill('flood')}
 />
 </ErrorBoundary>
 </div>
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
 <div id="chart-security-trend" className="rounded-xl transition-shadow">
 <ChartCard
 query={securityQ}
 isEmpty={secSeries.length === 0}
 emptyMessage="No security events in this period."
 name="Security Trend Chart"
 >
 <AreaChart
 data={secSeries}
 dataKeys={['authFailures', 'floodCount']}
 title="Rejected Sign-Ins & Probing Over Time"
 height={300}
 xAxisKey="timeGenerated"
 formatXAxis={xAxisFormatter}
 xAxisLabel={timeAxisLabel}
 yAxisLabel="Requests"
 seriesLabels={{ authFailures: 'Rejected sign-ins', floodCount: 'Probing traffic' }}
 exportFilename="security-trend"
 />
 </ChartCard>
 </div>
 <div id="chart-security-sources" className="rounded-xl transition-shadow">
 <ChartCard
 query={securityQ}
 isEmpty={secTopAuthData.length === 0}
 emptyMessage="No auth-failure sources in this period."
 name="Top Auth-Failure Sources Chart"
 >
 <BarChart
 data={secTopAuthData}
 dataKeys={['Failures']}
 xAxisKey="name"
 title="Top Sources of Rejected Sign-Ins"
 height={300}
 horizontal
 colorByIndex
 showValueLabels
 seriesLabels={{ Failures: 'Rejected requests' }}
 exportFilename="auth-failure-sources"
 />
 </ChartCard>
 </div>
 </div>
 </section>

 {/* Drill-down: the actual calls where a safety signal was caught.
 Opens from the Blocked / Flagged / Jailbreak KPI cards. Sourced from
 the gateway logs (the only table with the signal), NOT Conversations —
 a live probe confirmed the two don't share a join key. */}
 <SafetyEventsModal
 kind={safetyDrill}
 query={safetyEventsQ}
 timeRange={timeRange}
 onClose={() => setSafetyDrill(null)}
 />

 {/* AI-efficiency drill-down: the actual truncated / prompt-bloat calls,
 each openable to its full gateway log by Correlation ID. */}
 <EfficiencyEventsModal
 kind={effDrill}
 query={effEventsQ}
 timeRange={timeRange}
 onClose={() => setEffDrill(null)}
 />

 {/* Security drill-down: the actual auth-failure / 404-flood requests,
 each openable to its full gateway log by Correlation ID. */}
 <SecurityEventsModal
 kind={secDrill}
 query={secEventsQ}
 timeRange={timeRange}
 onClose={() => setSecDrill(null)}
 />

 {/* Reliability drill-down: the actual failing calls for a root-cause
 category, each openable to its full gateway log by Correlation ID. */}
 <ErrorTaxonomyEventsModal
 category={errDrill}
 query={errEventsQ}
 timeRange={timeRange}
 onClose={() => setErrDrill(null)}
 />
 </div>
 );
}

/* ───────────────────────── helpers ───────────────────────── */

/**
 * Shared responsive modal shell used by all drill-downs.
 * Mobile: full-width bottom sheet (rounded top, sticky header/footer).
 * ≥sm: centered dialog. Backdrop click + close button both dismiss.
 */
function DrillModalShell({ title, subtitle, footer, onClose, children, z = 'z-50', maxWidth = 'max-w-4xl' }) {
 return (
 <div
 className={`fixed inset-0 ${z} flex items-end sm:items-center justify-center bg-black/40 sm:p-4`}
 role="dialog"
 aria-modal="true"
 onClick={onClose}
 >
 <div
 className={`bg-white w-full ${maxWidth} rounded-t-2xl sm:rounded-xl shadow-xl max-h-[92vh] sm:max-h-[85vh] flex flex-col`}
 onClick={(e) => e.stopPropagation()}
 >
 <div className="flex items-start justify-between gap-3 px-4 sm:px-5 py-3 sm:py-4 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl sm:rounded-t-xl z-10">
 <div className="min-w-0">
 <h3 className="text-sm font-semibold text-gray-900 truncate">{title}</h3>
 {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
 </div>
 <button type="button" onClick={onClose} className="shrink-0 p-1.5 rounded-md hover:bg-gray-100 text-gray-500" aria-label="Close">
 <X className="w-4 h-4" />
 </button>
 </div>
 <div className="overflow-auto p-4 sm:p-5 grow">
 {children}
 </div>
 {footer && (
 <div className="px-4 sm:px-5 py-3 border-t border-gray-100 text-xs text-gray-500">
 {footer}
 </div>
 )}
 </div>
 </div>
 );
}

/** Centered empty/loading/error states shared by the events lists. */
function EventsStatus({ query, emptyIcon: Icon = ShieldCheck, emptyTitle, emptyHint }) {
 if (query?.isLoading) return <p className="text-sm text-gray-500 text-center py-10">Loading…</p>;
 if (query?.isError) return <p className="text-sm text-red-600 text-center py-10">Couldn't load. {query?.error?.message}</p>;
 return (
 <div className="flex flex-col items-center justify-center text-center py-10">
 <div className="p-3 rounded-full bg-emerald-50 mb-3"><Icon className="w-6 h-6 text-emerald-500" /></div>
 <p className="text-sm font-medium text-gray-700">{emptyTitle}</p>
 <p className="text-xs text-gray-500 mt-1 max-w-sm">{emptyHint}</p>
 </div>
 );
}

const sevColor = (s) => s === 'high' ? 'text-red-600 bg-red-50' : s === 'medium' ? 'text-amber-600 bg-amber-50' : 'text-gray-600 bg-gray-100';

/** Small pill used across the events lists. */
function Pill({ children, tone = 'gray' }) {
 const tones = {
 red: 'bg-red-50 text-red-600',
 amber: 'bg-amber-50 text-amber-600',
 gray: 'bg-gray-100 text-gray-600',
 brand: 'bg-brand-50 text-brand-600',
 };
 return <span className={`px-1.5 py-0.5 rounded ${tones[tone] || tones.gray}`}>{children}</span>;
}

/**
 * Modal listing the real calls where a content-safety signal fired.
 * Responsive: stacked cards on mobile, table ≥sm. Each row opens the full
 * gateway log (request/response + content-filter verdict) by Correlation ID.
 */
function SafetyEventsModal({ kind, query, timeRange, onClose }) {
 const [openCid, setOpenCid] = useState(null);
 const callQ = useApiQuery(
 ['content-safety-call', openCid, timeRange],
 () => api.getContentSafetyCall(openCid, timeRange),
 { enabled: !!openCid }
 );
 if (!kind) return null;
 const label = { blocked: 'Blocked', flagged: 'Flagged', jailbreak: 'Manipulation attempts', all: 'All safety signals' }[kind] || kind;
 const events = query?.data?.events || [];

 const caughtPills = (e) => (
 <span className="inline-flex flex-wrap items-center gap-1">
 {e.blocked && <Pill tone="red">blocked</Pill>}
 {e.jailbreak && <Pill tone="red">jailbreak</Pill>}
 {e.indirectAttack && <Pill tone="red">indirect</Pill>}
 {e.protectedMaterial && <Pill tone="amber">prot. material</Pill>}
 {e.category && !e.jailbreak && !e.indirectAttack && <Pill tone="amber">{e.category}</Pill>}
 {!e.blocked && !e.jailbreak && !e.indirectAttack && !e.protectedMaterial && !e.category && <span className="text-gray-400">signal</span>}
 </span>
 );

 return (
 <DrillModalShell
 title={`${label} — the actual calls`}
 subtitle="The specific calls where this was detected. Tap a call to see the full request and response."
 footer={<>{events.length > 0 && `${events.length} call${events.length === 1 ? '' : 's'} shown. `}Tap any call to open the full record.</>}
 onClose={onClose}
 >
 {(query?.isLoading || query?.isError || events.length === 0) ? (
 <EventsStatus
 query={query}
 emptyTitle={`No ${label.toLowerCase()} calls in this period`}
 emptyHint="Nothing matched in the selected time range. Widen the range if you expect events."
 />
 ) : (
 <>
 {/* Mobile: cards */}
 <ul className="sm:hidden space-y-2">
 {events.map((e, i) => (
 <li
 key={e.correlationId || i}
 className="border border-gray-100 rounded-lg p-3 active:bg-brand-50"
 onClick={() => e.correlationId && setOpenCid(e.correlationId)}
 >
 <div className="flex items-center justify-between">
 <span className="text-xs font-medium text-gray-800">{e.model || '—'}</span>
 {e.severity ? <span className={`text-xs px-1.5 py-0.5 rounded ${sevColor(e.severity)}`}>{e.severity}</span> : null}
 </div>
 <div className="mt-1 text-[11px] text-gray-500">{new Date(e.timeGenerated).toLocaleString()}</div>
 <div className="mt-1.5 text-xs">{caughtPills(e)}</div>
 <div className="mt-1.5 text-xs text-brand-600 font-medium">Open log →</div>
 </li>
 ))}
 </ul>
 {/* Desktop: table */}
 <table className="hidden sm:table w-full text-xs">
 <thead>
 <tr className="text-left text-gray-500 border-b border-gray-100">
 <th className="py-2 pr-3 font-medium">Time</th>
 <th className="py-2 pr-3 font-medium">AI Model</th>
 <th className="py-2 pr-3 font-medium">App / Account</th>
 <th className="py-2 pr-3 font-medium">What was detected</th>
 <th className="py-2 pr-3 font-medium">Severity</th>
 <th className="py-2 pr-3 font-medium">Details</th>
 </tr>
 </thead>
 <tbody>
 {events.map((e, i) => (
 <tr
 key={e.correlationId || i}
 className="border-b border-gray-50 hover:bg-brand-50 cursor-pointer"
 onClick={() => e.correlationId && setOpenCid(e.correlationId)}
 title="Open the full request & response for this call"
 >
 <td className="py-2 pr-3 whitespace-nowrap text-gray-700">{new Date(e.timeGenerated).toLocaleString()}</td>
 <td className="py-2 pr-3 text-gray-700">{e.model || '—'}</td>
 <td className="py-2 pr-3 text-gray-700">{e.productId || e.subscriptionId || '—'}</td>
 <td className="py-2 pr-3">{caughtPills(e)}</td>
 <td className="py-2 pr-3">{e.severity ? <span className={`px-1.5 py-0.5 rounded ${sevColor(e.severity)}`}>{e.severity}</span> : <span className="text-gray-400">—</span>}</td>
 <td className="py-2 pr-3"><span className="text-brand-600 font-medium">Open →</span></td>
 </tr>
 ))}
 </tbody>
 </table>
 </>
 )}
 <SafetyCallDetail cid={openCid} query={callQ} onClose={() => setOpenCid(null)} />
 </DrillModalShell>
 );
}

/**
 * Modal listing the real calls behind the AI-efficiency KPIs (truncated /
 * prompt-bloat). Responsive cards/table; each row opens the full gateway log
 * by Correlation ID (reusing the content-safety call-detail endpoint).
 */
function EfficiencyEventsModal({ kind, query, timeRange, onClose }) {
 const [openCid, setOpenCid] = useState(null);
 const callQ = useApiQuery(
 ['efficiency-call', openCid, timeRange],
 () => api.getContentSafetyCall(openCid, timeRange),
 { enabled: !!openCid }
 );
 if (!kind) return null;
 const label = { truncated: 'Cut-off answers', bloat: 'Oversized prompts', all: 'All calls' }[kind] || kind;
 const events = query?.data?.events || [];

 return (
 <DrillModalShell
 title={label}
 subtitle={kind === 'truncated'
 ? 'Answers that hit the length limit and stopped mid-response. Tap a call to open the full log.'
 : 'Calls that sent far more text than they got back — the biggest cost drivers. Tap a call to open the full log.'}
 footer={<>{events.length > 0 && `${events.length} call${events.length === 1 ? '' : 's'} shown. `}Tap any call to open the full record.</>}
 onClose={onClose}
 >
 {(query?.isLoading || query?.isError || events.length === 0) ? (
 <EventsStatus
 query={query}
 emptyTitle={`No ${label.toLowerCase()} in this period`}
 emptyHint="Nothing matched in the selected time range. Widen the range if you expect events."
 />
 ) : (
 <>
 {/* Mobile: cards */}
 <ul className="sm:hidden space-y-2">
 {events.map((e, i) => (
 <li
 key={e.correlationId || i}
 className="border border-gray-100 rounded-lg p-3 active:bg-brand-50"
 onClick={() => e.correlationId && setOpenCid(e.correlationId)}
 >
 <div className="flex items-center justify-between">
 <span className="text-xs font-medium text-gray-800">{e.model || '—'}</span>
 {kind === 'bloat' && e.ratio != null
 ? <Pill tone="amber">{e.ratio.toFixed(1)} : 1</Pill>
 : e.stopReason ? <Pill tone="red">{e.stopReason}</Pill> : null}
 </div>
 <div className="mt-1 text-[11px] text-gray-500">{new Date(e.timeGenerated).toLocaleString()}</div>
 <div className="mt-1.5 text-xs text-gray-600">{e.promptTokens.toLocaleString()} sent · {e.completionTokens.toLocaleString()} received</div>
 <div className="mt-1.5 text-xs text-brand-600 font-medium">Open log →</div>
 </li>
 ))}
 </ul>
 {/* Desktop: table */}
 <table className="hidden sm:table w-full text-xs">
 <thead>
 <tr className="text-left text-gray-500 border-b border-gray-100">
 <th className="py-2 pr-3 font-medium">Time</th>
 <th className="py-2 pr-3 font-medium">AI Model</th>
 <th className="py-2 pr-3 font-medium">Text sent</th>
 <th className="py-2 pr-3 font-medium">Text received</th>
 <th className="py-2 pr-3 font-medium">{kind === 'bloat' ? 'Sent vs. received' : 'Why it stopped'}</th>
 <th className="py-2 pr-3 font-medium">Details</th>
 </tr>
 </thead>
 <tbody>
 {events.map((e, i) => (
 <tr
 key={e.correlationId || i}
 className="border-b border-gray-50 hover:bg-brand-50 cursor-pointer"
 onClick={() => e.correlationId && setOpenCid(e.correlationId)}
 title="Open the full request & response for this call"
 >
 <td className="py-2 pr-3 whitespace-nowrap text-gray-700">{new Date(e.timeGenerated).toLocaleString()}</td>
 <td className="py-2 pr-3 text-gray-700">{e.model || '—'}</td>
 <td className="py-2 pr-3 text-gray-700">{e.promptTokens.toLocaleString()}</td>
 <td className="py-2 pr-3 text-gray-700">{e.completionTokens.toLocaleString()}</td>
 <td className="py-2 pr-3">
 {kind === 'bloat'
 ? (e.ratio != null ? <Pill tone="amber">{e.ratio.toFixed(1)} : 1</Pill> : '—')
 : (e.stopReason ? <Pill tone="red">{e.stopReason}</Pill> : '—')}
 </td>
 <td className="py-2 pr-3"><span className="text-brand-600 font-medium">Open →</span></td>
 </tr>
 ))}
 </tbody>
 </table>
 </>
 )}
 <SafetyCallDetail cid={openCid} query={callQ} onClose={() => setOpenCid(null)} />
 </DrillModalShell>
 );
}

/**
 * Modal listing the real requests behind the security KPIs (auth failures or
 * 404-flood hits). Responsive cards/table; each row opens the full gateway log
 * by Correlation ID (reusing the content-safety call-detail endpoint).
 */
function SecurityEventsModal({ kind, query, timeRange, onClose }) {
 const [openCid, setOpenCid] = useState(null);
 const callQ = useApiQuery(
 ['security-call', openCid, timeRange],
 () => api.getContentSafetyCall(openCid, timeRange),
 { enabled: !!openCid }
 );
 if (!kind) return null;
 const isFlood = kind === 'flood';
 const label = { auth: 'Rejected sign-ins', flood: 'Probing traffic' }[kind] || kind;
 const events = query?.data?.events || [];

 return (
 <DrillModalShell
 title={label}
 subtitle={isFlood
 ? 'Automated requests probing for endpoints that don\'t exist — typically scanners looking for weak spots. Tap a request to open the full record.'
 : 'Requests turned away because the key was missing, wrong, or not allowed. Tap a request to open the full record.'}
 footer={<>{events.length > 0 && `${events.length} request${events.length === 1 ? '' : 's'} shown. `}Tap any request to open the full record.</>}
 onClose={onClose}
 >
 {(query?.isLoading || query?.isError || events.length === 0) ? (
 <EventsStatus
 query={query}
 emptyTitle={`No ${label.toLowerCase()} in this period`}
 emptyHint="Nothing matched in the selected time range. Widen the range if you expect events."
 />
 ) : (
 <>
 {/* Mobile: cards */}
 <ul className="sm:hidden space-y-2">
 {events.map((e, i) => (
 <li
 key={e.correlationId || i}
 className="border border-gray-100 rounded-lg p-3 active:bg-brand-50"
 onClick={() => e.correlationId && setOpenCid(e.correlationId)}
 >
 <div className="flex items-center justify-between">
 <span className="text-xs font-medium text-gray-800 truncate">{e.ip || '—'}</span>
 <Pill tone={String(e.responseCode) === '403' ? 'red' : isFlood ? 'gray' : 'amber'}>{e.responseCode}</Pill>
 </div>
 <div className="mt-1 text-[11px] text-gray-500">{new Date(e.timeGenerated).toLocaleString()}</div>
 <div className="mt-1.5 text-xs text-gray-600 break-all">{e.method || ''} {e.url || '—'}</div>
 <div className="mt-1.5 text-xs text-brand-600 font-medium">Open log →</div>
 </li>
 ))}
 </ul>
 {/* Desktop: table */}
 <table className="hidden sm:table w-full text-xs">
 <thead>
 <tr className="text-left text-gray-500 border-b border-gray-100">
 <th className="py-2 pr-3 font-medium">Time</th>
 <th className="py-2 pr-3 font-medium">Came from (IP)</th>
 <th className="py-2 pr-3 font-medium">Result</th>
 <th className="py-2 pr-3 font-medium">What they asked for</th>
 {!isFlood && <th className="py-2 pr-3 font-medium">Account</th>}
 <th className="py-2 pr-3 font-medium">Why refused</th>
 <th className="py-2 pr-3 font-medium">Details</th>
 </tr>
 </thead>
 <tbody>
 {events.map((e, i) => (
 <tr
 key={e.correlationId || i}
 className="border-b border-gray-50 hover:bg-brand-50 cursor-pointer"
 onClick={() => e.correlationId && setOpenCid(e.correlationId)}
 title="Open the full request & response for this call"
 >
 <td className="py-2 pr-3 whitespace-nowrap text-gray-700">{new Date(e.timeGenerated).toLocaleString()}</td>
 <td className="py-2 pr-3 text-gray-700">{e.ip || '—'}</td>
 <td className="py-2 pr-3"><Pill tone={String(e.responseCode) === '403' ? 'red' : isFlood ? 'gray' : 'amber'}>{e.responseCode}</Pill></td>
 <td className="py-2 pr-3 text-gray-700 max-w-[22rem] truncate">{e.method || ''} {e.url || '—'}</td>
 {!isFlood && <td className="py-2 pr-3 text-gray-700">{e.subscriptionId || '—'}</td>}
 <td className="py-2 pr-3 text-gray-700">{e.errorReason || '—'}</td>
 <td className="py-2 pr-3"><span className="text-brand-600 font-medium">Open →</span></td>
 </tr>
 ))}
 </tbody>
 </table>
 </>
 )}
 <SafetyCallDetail cid={openCid} query={callQ} onClose={() => setOpenCid(null)} />
 </DrillModalShell>
 );
}

/**
 * Modal listing the real failing calls behind the reliability taxonomy KPIs.
 * `category` is an ErrorCategory label (or "all"). Responsive cards/table;
 * each row opens the full gateway log by Correlation ID.
 */
function ErrorTaxonomyEventsModal({ category, query, timeRange, onClose }) {
 const [openCid, setOpenCid] = useState(null);
 const callQ = useApiQuery(
 ['error-taxonomy-call', openCid, timeRange],
 () => api.getContentSafetyCall(openCid, timeRange),
 { enabled: !!openCid }
 );
 if (!category) return null;
 const label = category === 'all' ? 'All failed calls' : category;
 const events = query?.data?.events || [];
 const codeTone = (code) => {
 const c = Number(code);
 if (c >= 500) return 'red';
 if (c === 401 || c === 403) return 'amber';
 return 'gray';
 };

 return (
 <DrillModalShell
 title={`${label} — the actual calls`}
 subtitle="The specific calls that failed for this reason. Tap a call to see the full request and response."
 footer={<>{events.length > 0 && `${events.length} call${events.length === 1 ? '' : 's'} shown. `}Tap any call to open the full record.</>}
 onClose={onClose}
 >
 {(query?.isLoading || query?.isError || events.length === 0) ? (
 <EventsStatus
 query={query}
 emptyTitle={`No ${label.toLowerCase()} in this period`}
 emptyHint="Nothing matched in the selected time range. Widen the range if you expect events."
 />
 ) : (
 <>
 {/* Mobile: cards */}
 <ul className="sm:hidden space-y-2">
 {events.map((e, i) => (
 <li
 key={e.correlationId || i}
 className="border border-gray-100 rounded-lg p-3 active:bg-brand-50"
 onClick={() => e.correlationId && setOpenCid(e.correlationId)}
 >
 <div className="flex items-center justify-between">
 <span className="text-xs font-medium text-gray-800 truncate">{e.apiId || '—'}</span>
 <Pill tone={codeTone(e.responseCode)}>{e.responseCode}</Pill>
 </div>
 <div className="mt-1 text-[11px] text-gray-500">{new Date(e.timeGenerated).toLocaleString()}</div>
 {category === 'all' && <div className="mt-1.5 text-xs"><Pill tone="brand">{e.category}</Pill></div>}
 <div className="mt-1.5 text-xs text-gray-600 break-words">{e.reason || e.message || '—'}</div>
 <div className="mt-1.5 text-xs text-brand-600 font-medium">Open log →</div>
 </li>
 ))}
 </ul>
 {/* Desktop: table */}
 <table className="hidden sm:table w-full text-xs">
 <thead>
 <tr className="text-left text-gray-500 border-b border-gray-100">
 <th className="py-2 pr-3 font-medium">Time</th>
 <th className="py-2 pr-3 font-medium">AI Model / API</th>
 {category === 'all' && <th className="py-2 pr-3 font-medium">Cause</th>}
 <th className="py-2 pr-3 font-medium">Result</th>
 <th className="py-2 pr-3 font-medium">What went wrong</th>
 <th className="py-2 pr-3 font-medium">Details</th>
 </tr>
 </thead>
 <tbody>
 {events.map((e, i) => (
 <tr
 key={e.correlationId || i}
 className="border-b border-gray-50 hover:bg-brand-50 cursor-pointer"
 onClick={() => e.correlationId && setOpenCid(e.correlationId)}
 title="Open the full request & response for this call"
 >
 <td className="py-2 pr-3 whitespace-nowrap text-gray-700">{new Date(e.timeGenerated).toLocaleString()}</td>
 <td className="py-2 pr-3 text-gray-700">{e.apiId || '—'}</td>
 {category === 'all' && <td className="py-2 pr-3"><Pill tone="brand">{e.category}</Pill></td>}
 <td className="py-2 pr-3"><Pill tone={codeTone(e.responseCode)}>{e.responseCode}</Pill></td>
 <td className="py-2 pr-3 text-gray-700 max-w-[24rem] truncate" title={e.message || e.reason || ''}>{e.reason || e.message || '—'}</td>
 <td className="py-2 pr-3"><span className="text-brand-600 font-medium">Open →</span></td>
 </tr>
 ))}
 </tbody>
 </table>
 </>
 )}
 <SafetyCallDetail cid={openCid} query={callQ} onClose={() => setOpenCid(null)} />
 </DrillModalShell>
 );
}

/**
 * Full request/response for one call, fetched by Correlation ID from the
 * gateway logs — the "see the logs using the ID" view. Responsive: stacks on
 * mobile, 4-col meta grid ≥sm.
 */
function SafetyCallDetail({ cid, query, onClose }) {
 if (!cid) return null;
 const d = query?.data;
 const pretty = (obj, raw) => (obj ? JSON.stringify(obj, null, 2) : (raw || '(empty)'));
 return (
 <DrillModalShell
 title="Full call record"
 subtitle={`Reference ID: ${cid}`}
 onClose={onClose}
 z="z-[60]"
 maxWidth="max-w-5xl"
 >
 {query?.isLoading ? (
 <p className="text-sm text-gray-500 text-center py-10">Loading log…</p>
 ) : query?.isError ? (
 <p className="text-sm text-red-600 text-center py-10">
 Couldn't load the record. {query?.error?.message || 'It may be older than we keep detailed logs for.'}
 </p>
 ) : d ? (
 <div className="space-y-4">
 <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
 <Meta label="Time" value={new Date(d.timeGenerated).toLocaleString()} />
 <Meta label="API" value={d.apiId} />
 <Meta label="Result" value={`${d.responseCode}${d.backendResponseCode ? ` / ${d.backendResponseCode}` : ''}`} />
 <Meta label="Response time" value={d.totalTimeMs != null ? `${d.totalTimeMs} ms` : '—'} />
 <Meta label="App / Product" value={d.productId || '—'} />
 <Meta label="Account" value={d.subscriptionId || '—'} />
 <Meta label="Method" value={d.method || '—'} />
 <Meta label="Address (URL)" value={d.url || '—'} />
 </div>
 <div>
 <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Request (what was sent)</p>
 <pre className="text-[11px] bg-gray-50 border border-gray-100 rounded-lg p-3 overflow-auto max-h-56 sm:max-h-64 whitespace-pre-wrap break-words">
 {pretty(d.requestBody, d.requestBodyRaw)}
 </pre>
 </div>
 <div>
 <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Response (what came back, incl. safety check)</p>
 <pre className="text-[11px] bg-gray-50 border border-gray-100 rounded-lg p-3 overflow-auto max-h-72 sm:max-h-80 whitespace-pre-wrap break-words">
 {pretty(d.responseBody, d.responseBodyRaw)}
 </pre>
 </div>
 </div>
 ) : (
 <p className="text-sm text-gray-500 text-center py-10">No detail available.</p>
 )}
 </DrillModalShell>
 );
}

function Meta({ label, value }) {
 return (
 <div>
 <p className="text-gray-400">{label}</p>
 <p className="text-gray-800 font-medium break-all">{value}</p>
 </div>
 );
}

function KPISkeleton() {
 return (
 <div className="kpi-card animate-pulse" aria-hidden="true">
 <div className="flex items-start justify-between mb-3">
 <div className="h-3 w-20 bg-gray-200 rounded" />
 <div className="h-8 w-8 bg-gray-200 rounded-lg" />
 </div>
 <div className="h-7 w-24 bg-gray-200 rounded mb-3" />
 <div className="h-3 w-16 bg-gray-200 rounded" />
 </div>
 );
}

function ChartCard({ children, query, isLoading, isError, isEmpty, emptyMessage, name }) {
 const loading = isLoading ?? query?.isLoading;
 const error = isError ?? query?.isError;
 const empty = isEmpty ?? (Array.isArray(query?.data) ? query.data.length === 0 : false);

 if (loading) {
 return <ChartSkeleton />;
 }
 if (error) {
 return (
 <ErrorState
 title="Couldn't load chart"
 message={query?.error?.message ||'Try again in a moment.'}
 onRetry={query?.refetch}
 />
 );
 }
 if (empty) {
 return <EmptyState message={emptyMessage} />;
 }
 return <ErrorBoundary name={name}>{children}</ErrorBoundary>;
}

function ChartSkeleton() {
 return (
 <div className="chart-container animate-pulse" aria-hidden="true">
 <div className="flex items-center justify-between mb-4">
 <div className="h-4 w-40 bg-gray-200 rounded" />
 <div className="h-6 w-16 bg-gray-200 rounded" />
 </div>
 <div className="h-[260px] w-full bg-gray-100 rounded-lg" />
 </div>
 );
}

function EmptyState({ message }) {
 return (
 <div className="chart-container flex flex-col items-center justify-center text-center min-h-[300px]">
 <div className="p-3 rounded-full bg-gray-100 mb-3">
 <Inbox className="w-5 h-5 text-gray-400" />
 </div>
 <p className="text-sm text-gray-500">{message ||'No data available.'}</p>
 </div>
 );
}

function ErrorState({ title, message, onRetry }) {
 return (
 <div className="chart-container flex flex-col items-center justify-center text-center min-h-[300px]">
 <div className="p-3 rounded-full bg-red-50 mb-3">
 <AlertTriangle className="w-5 h-5 text-red-500" />
 </div>
 <p className="text-sm font-medium text-gray-700">{title}</p>
 {message && <p className="text-xs text-gray-500 mt-1 max-w-xs">{message}</p>}
 {onRetry && (
 <button
 type="button"
 onClick={onRetry}
 className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md
 bg-white border border-gray-200 
 text-gray-700 hover:bg-gray-50 transition-colors"
 >
 <RefreshCw className="w-3 h-3" />
 Retry
 </button>
 )}
 </div>
 );
}
